import * as React from "react";
import styled, { withTheme, ThemeProps } from "styled-components";
import ReactModal from "react-modal";
import { injectIntl, useIntl, WrappedComponentProps } from "react-intl";
import { Routine } from "redux-saga-routines";
import { Action, ActionFunctionAny } from "redux-actions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';

import { connect, useDispatch } from "react-redux";
import { Button } from "react-bootstrap";
import { startLocalLearning, stopLocalLearning, addLocalLearning, addSensor, fetchSensors } from "@Safemate/Settings/Store/routines";
import confirmModal, { ModalTitle, H2, ModalBody, ModalFooter, CancelButton } from "@Safemate/Modals/confirmModal";
import { setModalStatus } from "@Safemate/Store/actions";
import { ITheme } from "@Theme/types";
import { ButtonWrap, Centered, DescriptionParagraph, HeaderRow, LoaderWrap, SensorHeader, SensorWrapper, Wrapper } from "./styles";
import { BackWrapper } from "@Safemate/UserAdmin/Users/Edit/styles";
import LocalLearningSensor from "./localLearningSensor";
import { ILocalLearningSensor, ISensorDTO, LocalLearningEventDO } from "../types";
import { Client, IMessage } from "@stomp/stompjs";
import { connectWebsocket } from "@Safemate/Websocket";
import Loader from "@Elements/Loader";
import { useHistory } from "react-router";
import { AppState } from "@Safemate/Store/types";
import { FullWidthInput } from "@Safemate/SubUnits/Form/styled";
import { addSensor as addSensorApi } from "@Safemate/Settings/Store/api";


const mapStateToProps = ( { 
  settings: {
    device: { device: {deviId}},
    sensor: { sensors, learningRemaining, learningMode, startingLearningMode, endingLearningMode, addedLocalLearning }
  }
}: AppState ) => {
  return {
    deviId,
    learningRemaining,
    learningMode,
    startingLearningMode, 
    endingLearningMode,
    sensors,
    addedLocalLearning
  }
}

const mapDispatchToProps = {
  startLocalLearning,
  stopLocalLearning,
  addLocalLearning,
}

interface LocalLearningProps {
  startLocalLearning: Routine<ActionFunctionAny<Action<any>>>;
  stopLocalLearning: Routine<ActionFunctionAny<Action<any>>>;
  addLocalLearning: Routine<ActionFunctionAny<Action<any>>>;
  deviId: number;
  learningRemaining: number;
  learningMode: boolean;
  startingLearningMode: boolean; 
  endingLearningMode: boolean;
  sensors: ISensorDTO[];
  returnPath?: string;
  addedLocalLearning: String[];
}

interface SensorList{
  [index: string]: ILocalLearningSensor;
}

const LocalLearning = connect(mapStateToProps, mapDispatchToProps)(
    ({ 
      returnPath, 
      startLocalLearning, 
      stopLocalLearning, 
      addLocalLearning, 
      deviId, 
      sensors, 
      learningRemaining,
      learningMode,
      startingLearningMode,
      endingLearningMode,
      addedLocalLearning
    }: LocalLearningProps) => {

      const { formatMessage } = useIntl();
      const history = useHistory();

      const [ websocket, setWebsocket ] = React.useState<Client>();
      const [ learnSensors, setLearnSensors ] = React.useState<SensorList>({});
      const [ learnTimeout, setLearnTimeout ] = React.useState<NodeJS.Timeout>();

      let sensorStorage: SensorList = {};

      React.useEffect(() =>{
        return () => {
          stopLearn();
          stopSocket();
        }
      }, [])

      React.useEffect(() => {
        clearTimeout(learnTimeout);
        if(learningRemaining > 0){
          const timeout = setTimeout(() => {
            stopSocket();
            stopLearn();
          }, learningRemaining * 1000)
          setLearnTimeout(timeout);
        }
      }, [learningRemaining])
      

      const start = () => {
        startSocket();
        startLocalLearning(deviId);
      }

      const stop = (exit?: boolean) => {
        stopSocket();
        stopLearn();
        sensorStorage = {};
        setLearnSensors({});
        if(exit){
          history.push(returnPath)
        }
      }

      const startSocket = () => {

        if(websocket){
          websocket.deactivate();
        }

        const socket = connectWebsocket();

        if(socket.connected){
          socket.subscribe(`/topic/locallearning/${deviId}`, (message: IMessage) => {
            processMessage(message);
          });
        }
        else{
          socket.onConnect = (frame) => {
            socket.subscribe(`/topic/locallearning/${deviId}`, (message: IMessage) => {
              processMessage(message);
            });
          }
        }
        
        if(!socket.active){
          socket.activate();
        }

        socket.onDisconnect = () => {
          stop();
        }

        setWebsocket(socket);
      };

      const stopSocket = () => {
        if (websocket && websocket.active) {
          websocket.unsubscribe(`/topic/locallearning/${deviId}`)
        }
      };

      const stopLearn = () => {
        if(learningMode)
          stopLocalLearning(deviId);
      }

      const handleAdd = (sensor: ILocalLearningSensor) => {
        addLocalLearning({
          deviId,
          gatewayId: sensor.id,
          rssi: sensor.rssi
        });
        const sensorState = {...learnSensors};
        sensorState[sensor.id].pending = true;
        setLearnSensors(sensorState);
      };

      const processMessage = (message: IMessage) => {
        try {
          const event: LocalLearningEventDO = JSON.parse(message.body);
          if (event && event.sensors && event.sensors.length > 0) {
            event.sensors.forEach((sensor: ILocalLearningSensor) => {
              if (sensor.id) {
                sensorStorage[sensor.id] = { ...sensorStorage[sensor.id], ...sensor };
                setLearnSensors({...sensorStorage});
              } else {
                console.warn(`Missing id: ${message}`);
              }
            });
          }
        } catch (e) {
          console.error(`Could not process device stream message: ${e}`);
        }
      };
      
      const sensorIds = Object.keys(learnSensors);

      const existingSensors: ILocalLearningSensor[] = [];
      const newSensors: ILocalLearningSensor[] = [];

      const unaddedSensors = Object.values(learnSensors).filter(s => s.added !== true)


      sensorIds.forEach(sensor => {
        const sensorData = learnSensors[sensor];
        if (sensorData.added && !addedLocalLearning.includes(sensorData.id)) {
          existingSensors.push(sensorData);
        } else {
          newSensors.push(sensorData);
        }
      });

      const {Comp, func} = confirmModal();

      return (
        <Wrapper>
          <HeaderRow>
            <h1>
              {formatMessage({id: "localLearningPageTitle", defaultMessage: "Lokal Innlæring"})}
            </h1>
          </HeaderRow>
          <Description/>
          {learningMode &&
          <React.Fragment>
          {
            newSensors.length === 0 &&
            existingSensors.length === 0 ?
            (
              <WaitingForSensors/>
            ) :
            (
              <SensorWrapper>
                {(newSensors.length > 0) &&
                <Centered>
                  <SensorHeader>
                    {formatMessage({id: "newSensors", defaultMessage: "Nye sensorer"})}:
                  </SensorHeader>
                </Centered>}
                {newSensors.map(sensor => {
                  let successfullyAdded = false;
                  sensors.forEach(item => {
                    if(item.sensor.gatewayId === sensor.id){
                      successfullyAdded = true;
                    }
                  })

                  return (
                    <LocalLearningSensor
                      existing={false}
                      key={sensor.id}
                      handleAdd={handleAdd}
                      sensor={sensor}
                      successfullyAdded={addedLocalLearning.includes(sensor.id)}
                    />
                  )
                })}
                {existingSensors.length > 0 &&
                  <Centered>
                    <SensorHeader>
                      {formatMessage({id: "existingSensors", defaultMessage: "Eksisterende sensorer"})}
                    </SensorHeader>
                    {existingSensors.map(sensor => {
                      return(
                        <LocalLearningSensor
                          existing={true}
                          key={sensor.id}
                          sensor={sensor}
                        />
                      )
                    })}
                  </Centered>
                }
              </SensorWrapper>
            )
          }
          </React.Fragment>
          }
          <ButtonWrap
          >
            <Cancel stop={stop} push={history.push} back={returnPath} showReminder={unaddedSensors.length !== 0}/>
            {learningMode ? 
              endingLearningMode ?
              <LoaderWrap><Loader/></LoaderWrap> :
              <Button
                onClick={() => {
                    unaddedSensors.length === 0
                      ? stop(true)
                      : func(true)
                  }
                }
              >
                {formatMessage({id: 'localLearningCompleteBtn', defaultMessage: 'Fullfør Lokal innlæring'})}
              </Button> :
              startingLearningMode ?
              <LoaderWrap><Loader/></LoaderWrap> :
              <Button
                onClick={() => {
                  start();
                }}
              >
                {formatMessage({id: "startLocalLearning", defaultMessage: "Start innlæring"})}
              </Button>}
              <AddManual/>
          </ButtonWrap>
          <Reminder
            Modal={Comp}
            confirm={() => {
              stop(true);
              func(false);
            }}
          />
        </Wrapper>
      )
    }
)

interface HeaderProps extends WrappedComponentProps{
  back: string;
  push: Function;
  showReminder: boolean;
  stop: Function;
}

const Cancel = injectIntl(({ intl: { formatMessage }, stop, back, push, showReminder}: HeaderProps) => {

  const {Comp, func} = confirmModal();

  return(
    <React.Fragment>
      <BackWrapper>
        <Button onClick={() => {
          if(showReminder){
            func(true);
          }
          else{
            stop();
            push(back);
          }}}>
          {formatMessage({id: "cancel", defaultMessage: "Avbryt"})}
        </Button>
      </BackWrapper>
      <Reminder 
        Modal={Comp}
        confirm={() => {
          stop();
          push(back)
          func(false)
        }}
      />
    </React.Fragment>
  )
})

const InputWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin: 7.5px 0px;
`;

const FieldLabel = styled.label`
  margin-bottom: 4px;
`;

const ValidationError = styled.span`
  color: #F12638;
  font-size: 13px;
  margin-top: 4px;
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

interface AddManualModalProps extends ThemeProps<ITheme>, WrappedComponentProps {
  isOpen: boolean;
  serialNumber: string;
  name: string;
  saving: boolean;
  showValidation: boolean;
  onSerialNumberChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

// Defined at module scope so the component identity is stable across renders of
// the parent. Previously this modal was produced by calling confirmModal() inside
// the parent's render, which created a brand new component type on every keystroke
// and forced React to remount the inputs — causing focus to be lost after every
// character typed.
const AddManualModal = injectIntl(withTheme(({
  theme,
  intl: { formatMessage },
  isOpen,
  serialNumber,
  name,
  saving,
  showValidation,
  onSerialNumberChange,
  onNameChange,
  onCancel,
  onConfirm
}: AddManualModalProps) => {

  const serialNumberValid = serialNumber.trim().length > 0;
  const nameValid = name.trim().length > 0;
  const isValid = serialNumberValid && nameValid;

  const submit = () => {
    if(isValid && !saving){
      onConfirm();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if(event.key === "Enter"){
      event.preventDefault();
      onConfirm();
    }
  };

  return(
    <ReactModal
      isOpen={isOpen}
      onRequestClose={() => { if(!saving) onCancel(); }}
      style={{
        content: {
          maxWidth: "80%",
          maxHeight: "80vh",
          minWidth: "400px",
          height: "fit-content",
          position: "unset",
          background: theme.colors.backgroundPrimary,
          color: ""
        },
        overlay: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 5
        }
      }}
    >
      <ModalTitle>
        <H2>{formatMessage({id: "addSerialNumber", defaultMessage: "Legg til Serienummer"})}</H2>
      </ModalTitle>
      <ModalBody>
        <p>
          <span>{formatMessage({id: "switchUnitDesc", defaultMessage: "Skriv inn serienummer på den nye enheten du skal ta i bruk. Alle innstillinger (varslingsliste, geofence, etc.) vil bevares."})}</span>
        </p>
        <InputWrap>
          <FieldLabel htmlFor="serialNumber">{formatMessage({id: "serialNumber", defaultMessage: "Serial Number"})}</FieldLabel>
          <FullWidthInput
            id="serialNumber"
            value={serialNumber}
            disabled={saving}
            autoFocus
            onChange={(event) => onSerialNumberChange(event.currentTarget.value)}
            onKeyDown={handleKeyDown}
            type="text"
          />
          {showValidation && !serialNumberValid &&
            <ValidationError>{formatMessage({id: "serialNumberRequired", defaultMessage: "Serienummer er påkrevd"})}</ValidationError>}
        </InputWrap>
        <InputWrap>
          <FieldLabel htmlFor="name">{formatMessage({id: "name", defaultMessage: "Name"})}</FieldLabel>
          <FullWidthInput
            id="name"
            value={name}
            disabled={saving}
            onChange={(event) => onNameChange(event.currentTarget.value)}
            onKeyDown={handleKeyDown}
            type="text"
          />
          {showValidation && !nameValid &&
            <ValidationError>{formatMessage({id: "nameRequired", defaultMessage: "Navn er påkrevd"})}</ValidationError>}
        </InputWrap>
      </ModalBody>
      <ModalFooter>
        <CancelButton disabled={saving} onClick={() => { if(!saving) onCancel(); }}>
          {formatMessage({id: "cancel", defaultMessage: "Avbryt"})}
        </CancelButton>
        <Button disabled={saving || !isValid} onClick={submit}>
          <ButtonContent>
            {saving
              ? <FontAwesomeIcon icon={faSpinner} spin/>
              : formatMessage({id: "confirm", defaultMessage: "Bekreft"})}
          </ButtonContent>
        </Button>
      </ModalFooter>
    </ReactModal>
  );
}));

const mapStateToAddManualProps = ({ settings: { initialization: { deviId }, sensor: { savingSensor, addSensorFailed } } }: AppState) => {
  return {
    deviId,
    savingSensor,
    addSensorFailed
  }
}

const mapDispatchToAddManualProps = {
  addSensor,
  fetchSensors
}

interface AddManualProps{
  deviId: number
  savingSensor: boolean;
  addSensorFailed: boolean;
  addSensor: Routine<ActionFunctionAny<Action<any>>>;
  fetchSensors: Routine<ActionFunctionAny<Action<any>>>;
}

const AddManual = connect(mapStateToAddManualProps, mapDispatchToAddManualProps)(({deviId, savingSensor, addSensorFailed, addSensor, fetchSensors}: AddManualProps) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [ open, setOpen ] = React.useState(false);
  const [ serialNumber, setSerialNumber ] = React.useState("");
  const [ name, setName ] = React.useState("");
  const [ submitted, setSubmitted ] = React.useState(false);

  const wasSaving = React.useRef(false);

  const setModal = (status: boolean) => {
    setOpen(status);
    dispatch(setModalStatus(status));
  };

  const reset = () => {
    setSerialNumber("");
    setName("");
    setSubmitted(false);
  };

  const openModal = () => {
    reset();
    setModal(true);
  };

  const closeModal = () => {
    if(savingSensor) return;
    setModal(false);
    reset();
  };

  // React to the save completing. The request is in flight while savingSensor is
  // true; when it flips back to false we either close + refresh the sensor list
  // on success, or keep the dialog open on failure so the user can correct their
  // input (the global feedback toast explains what went wrong).
  React.useEffect(() => {
    if(wasSaving.current && !savingSensor && open){
      if(!addSensorFailed){
        setModal(false);
        reset();
        fetchSensors({deviId});
      }
    }
    wasSaving.current = savingSensor;
  }, [savingSensor, addSensorFailed, open, deviId]);

  const add = () => {
    setSubmitted(true);
    const trimmedSerialNumber = serialNumber.trim();
    const trimmedName = name.trim();
    if(trimmedSerialNumber.length > 0 && trimmedName.length > 0 && !savingSensor){
      addSensor({deviId, serialNumber: trimmedSerialNumber, name: trimmedName});
    }
  };

  return(
    <>
      <Button onClick={openModal}>
        {formatMessage({id: "addSerialNumber", defaultMessage: "Legg til Serienummer"})}
      </Button>
      <AddManualModal
        isOpen={open}
        serialNumber={serialNumber}
        name={name}
        saving={savingSensor}
        showValidation={submitted}
        onSerialNumberChange={setSerialNumber}
        onNameChange={setName}
        onCancel={closeModal}
        onConfirm={add}
      />
    </>
  );
})

interface ReminderProps{
  confirm: Function;
  Modal: React.FunctionComponent<any>;
}

const Reminder = ({confirm, Modal}: ReminderProps) => {

  const { formatMessage } = useIntl();

  return(
    <Modal
      title={formatMessage({id: "localLearningCompleteBtn", defaultMessage: "Fullfør Lokal innlæring"})}
      body={
        <p>
          {formatMessage({id: 'notAddedSensText', defaultMessage: 'Sensorer har blitt oppdaget men ikke lagt til. Disse sensorene kan bli lagt til ved å trykke på pluss-tegnet ved siden av gjeldende sensor. Dersom du ønsker å fortsette uten å legge de til, kan du trykke på "Fullfør Lokal innlæring".'})}
        </p>
      }
      confirmText={formatMessage({id: "localLearningCompleteBtn", defaultMessage: "Fullfør Lokal innlæring"})}
      confirmFunc={confirm}
    />
  )
}

const Description = injectIntl(({intl: { formatMessage }}: WrappedComponentProps) => {
  return(
    <div>
      <DescriptionParagraph>
        {formatMessage({id: 'localLearningDescription', defaultMessage: 'Trykk på "Pluss"-ikonet ved siden av sensoren for å legge til enheten.'})}
      </DescriptionParagraph>
    </div>
  )
})

const WaitingDiv = styled.div`
  text-align: center;
  font-size: 20px;
  font-weight: 500;
`;

const SpinnerDiv = styled.div`
  margin: 0;
  animation: rotation 1s infinite linear;
  color: ${props => props.theme.colors.backgroundPrimary};
  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
    svg {
      color: ${props => props.theme.colors.backgroundPrimary};
    }
  }
`;

const RotatingIcon = styled(FontAwesomeIcon)`
  width: 1em;
  height: 1em;
`;

interface WaitingForSensorsProps extends WrappedComponentProps {

}

const WaitingForSensors = injectIntl(({ intl: { formatMessage }}: WaitingForSensorsProps) => {
  return(
    <WaitingDiv>
      {formatMessage({id: 'localLearningWaitingText', defaultMessage: 'Venter på sensorkommunikasjon...'})}
      <SpinnerDiv>
        <RotatingIcon
          icon={faSpinner}
          style={{ color: '#cacacb' }}
        />
      </SpinnerDiv>
    </WaitingDiv>
  )
})

export default LocalLearning;